import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { t } from '../lib/i18n';
import { Skeleton } from '../components/Skeleton';
import { Sparkline } from '../components/Sparkline';
import { RadarPolygon } from '../components/RadarPolygon';
import { ChannelDonut } from '../components/ChannelDonut';
import { IndicatorsGrid } from '../components/IndicatorsGrid';
import { Fab } from '../components/Fab';
import { apiClient, ApiError } from '../lib/api-client';

// =========================================================
// Tipos alinhados ao contrato GET /risk/radar
// =========================================================

interface AlertMetric {
  readonly label: string;
  readonly value: string;
  readonly deltaPct: number | null;
}

interface RadarAlert {
  readonly id: string;
  readonly severity: 'danger' | 'warning' | 'ok';
  readonly title: string;
  readonly diagnosis: string;
  readonly recommendation: string | null;
  readonly metric: AlertMetric | null;
  readonly series: readonly number[] | null;
  readonly sources: readonly string[];
}

interface RadarPeriod {
  readonly label: string;
  readonly startIso: string;
  readonly endIso: string;
}

interface RadarIndicators {
  readonly total: number;
  readonly normal: number;
  readonly verificar: number;
  readonly atencao: number;
}

interface RadarDimension {
  readonly key: string;
  readonly label: string;
  readonly score: number;
}

interface RadarChannel {
  readonly name: string;
  readonly reach: number;
}

interface IndicatorListItem {
  readonly id: string;
  readonly label: string;
  readonly value: string;
  readonly group: 'atencao' | 'verificar' | 'saudavel';
  readonly source: string;
  readonly series: readonly number[] | null;
  readonly deltaPct: number | null;
}

interface RadarSnapshot {
  readonly score: number | null;
  readonly scoreBand: 'bom' | 'atencao' | 'critico' | null;
  readonly trend: null;
  readonly scoreTrend: null;
  readonly period: RadarPeriod | null;
  readonly connectedSources: readonly string[];
  readonly indicators: RadarIndicators | null;
  readonly alerts: readonly RadarAlert[];
  readonly dimensions: readonly RadarDimension[] | null;
  readonly channels: readonly RadarChannel[] | null;
  readonly indicatorsList: readonly IndicatorListItem[];
}

type SeverityFilter = 'all' | 'danger' | 'warning' | 'ok';

type LoadState =
  | { kind: 'loading' }
  | { kind: 'coldStart' }
  | { kind: 'ready'; data: RadarSnapshot }
  | { kind: 'error' };

/**
 * Cold start: sem score e sem alertas.
 * Regra de ouro: nunca fabricar numero — degrade honesto.
 */
function isColdStart(data: RadarSnapshot): boolean {
  return data.score === null && (data.alerts ?? []).length === 0;
}

// =========================================================
// Sub-componentes internos (sem 2 casos de uso = sem abstracao)
// =========================================================

/** Badge de severidade no card de alerta */
function SeverityBadge({
  severity,
}: {
  readonly severity: 'danger' | 'warning' | 'ok';
}): JSX.Element {
  const label =
    severity === 'danger'
      ? t('radar.alertSevDanger')
      : severity === 'warning'
        ? t('radar.alertSevWarning')
        : t('radar.alertSevOk');
  return (
    <span className={`radar-alert-badge radar-alert-badge-${severity}`} aria-label={label}>
      {label}
    </span>
  );
}

/** Linha de variacao (deltaPct) */
function DeltaBadge({ deltaPct }: { readonly deltaPct: number }): JSX.Element {
  const isUp = deltaPct > 0;
  const isDown = deltaPct < 0;
  const arrow = isUp ? '▲' : isDown ? '▼' : '—';
  const className = isUp
    ? 'radar-delta radar-delta-up'
    : isDown
      ? 'radar-delta radar-delta-down'
      : 'radar-delta radar-delta-flat';
  return (
    <span className={className}>
      {arrow} {Math.abs(deltaPct).toFixed(1)}%
    </span>
  );
}

/** Card individual de alerta — linha densa (prototipo §alert-row) */
function AlertCard({
  alert,
  index,
  onExplain,
}: {
  readonly alert: RadarAlert;
  readonly index: number;
  readonly onExplain: (id: string) => void;
}): JSX.Element {
  const hasSeries = alert.series !== null && alert.series.length >= 2;

  // Ícone por severidade conforme prototipo
  const icon =
    alert.severity === 'danger'
      ? 'trending_down'
      : alert.severity === 'warning'
        ? 'help_outline'
        : 'check_circle';

  const iconBg =
    alert.severity === 'danger'
      ? 'rgba(192,57,43,.12)'
      : alert.severity === 'warning'
        ? 'rgba(193,122,0,.12)'
        : 'rgba(46,125,82,.12)';

  const iconColor =
    alert.severity === 'danger' ? '#C0392B' : alert.severity === 'warning' ? '#C17A00' : '#2E7D52';

  return (
    <article
      className={`radar-alert-row radar-alert-row-${alert.severity}`}
      data-sev={alert.severity}
      aria-label={alert.title}
      tabIndex={0}
      role="button"
      onClick={() => onExplain(alert.id)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onExplain(alert.id);
        }
      }}
      style={{ animationDelay: `${index * 60}ms` }}
    >
      {/* Coluna 1: filete semântico 4px */}
      <div className="radar-alert-row-sev-bar" aria-hidden="true" />

      {/* Coluna 2: ícone 36px */}
      <div className="radar-alert-row-icon-wrap" style={{ background: iconBg }} aria-hidden="true">
        <span
          className="material-symbols-rounded"
          style={{
            fontSize: 22,
            color: iconColor,
            fontVariationSettings: "'FILL' 1,'wght' 600,'GRAD' 0,'opsz' 24",
          }}
        >
          {icon}
        </span>
      </div>

      {/* Coluna 3: corpo */}
      <div className="radar-alert-row-body">
        <SeverityBadge severity={alert.severity} />
        <p className="radar-alert-row-title">{alert.title}</p>
        <p className="radar-alert-row-why">{alert.diagnosis}</p>
        {alert.recommendation !== null ? (
          <p className="radar-alert-row-action-text">
            <span aria-hidden="true">&rarr;</span> {alert.recommendation}
          </p>
        ) : null}
        <button
          type="button"
          className="radar-alert-row-cta"
          onClick={(e) => {
            e.stopPropagation();
            onExplain(alert.id);
          }}
        >
          <span className="material-symbols-rounded" aria-hidden="true" style={{ fontSize: 12 }}>
            forum
          </span>
          {t('radar.explainWithCross')}
        </button>
      </div>

      {/* Coluna 4: sparkline — só se series != null */}
      {hasSeries ? (
        <div className="radar-alert-row-spark" aria-hidden="true">
          <Sparkline points={alert.series as number[]} width={120} height={40} ariaLabel="" />
        </div>
      ) : (
        <div className="radar-alert-row-spark" />
      )}

      {/* Coluna 5: valor + variação — só se metric != null */}
      {alert.metric !== null ? (
        <div className="radar-alert-row-val-col">
          <span className="radar-alert-row-val" style={{ color: iconColor }}>
            {alert.metric.value}
          </span>
          {alert.metric.deltaPct !== null ? <DeltaBadge deltaPct={alert.metric.deltaPct} /> : null}
          <span className="radar-alert-row-val-label">{alert.metric.label}</span>
        </div>
      ) : (
        <div className="radar-alert-row-val-col" />
      )}

      {/* Overlay hover */}
      <div className="radar-alert-row-hover" aria-hidden="true" />
    </article>
  );
}

// =========================================================
// Bloco 2 — card Radar de dimensões
// =========================================================

function DimensionsCard({
  dimensions,
  connectedSources,
  onDimClick,
}: {
  readonly dimensions: readonly RadarDimension[] | null;
  readonly connectedSources: readonly string[];
  readonly onDimClick: (key: string) => void;
}): JSX.Element {
  const n = dimensions?.length ?? 0;
  return (
    <div className="radar-e-card">
      <div className="radar-e-card-inner">
        <div className="radar-editorial-kicker">{t('radar.dimensionsKicker')}</div>
        <h2 className="radar-editorial-headline">
          {t('radar.dimensionsHeadline', { n: String(n) })}
        </h2>
        <p className="radar-editorial-guide">{t('radar.dimensionsGuide')}</p>

        {dimensions !== null && dimensions.length > 0 ? (
          <>
            <RadarPolygon dimensions={dimensions} onDimClick={onDimClick} />
            {/* Rodapé */}
            <div className="radar-card-footer">
              <span className="radar-card-footer-info">
                <span
                  className="material-symbols-rounded"
                  aria-hidden="true"
                  style={{
                    fontSize: 13,
                    color: 'var(--md-primary)',
                    fontVariationSettings: "'FILL' 0,'wght' 500,'GRAD' 0,'opsz' 20",
                  }}
                >
                  info
                </span>
                {connectedSources.join(' · ')}
              </span>
              <span className="radar-card-footer-hint">
                <span
                  className="material-symbols-rounded"
                  aria-hidden="true"
                  style={{
                    fontSize: 13,
                    color: 'var(--md-primary)',
                    fontVariationSettings: "'FILL' 0,'wght' 500,'GRAD' 0,'opsz' 20",
                  }}
                >
                  touch_app
                </span>
                {t('radar.dimensionsFooter')}
              </span>
            </div>
          </>
        ) : (
          <p className="radar-null-state">{t('radar.dimensionsNull')}</p>
        )}
      </div>
    </div>
  );
}

// =========================================================
// Bloco 2 — card Achados (findings)
// =========================================================

function FindingsCard({
  alerts,
  onExplain,
}: {
  readonly alerts: readonly RadarAlert[];
  readonly onExplain: (id: string) => void;
}): JSX.Element {
  const count = alerts.length;
  return (
    <div className="radar-e-card radar-findings-card">
      <div className="radar-e-card-inner radar-e-card-inner-compact">
        <div className="radar-editorial-kicker">
          {t('radar.findingsKicker', { n: String(count) })}
        </div>
        <h2 className="radar-editorial-headline">{t('radar.findingsHeadline')}</h2>

        {count === 0 ? (
          <p className="radar-null-state">{t('radar.findingsEmpty')}</p>
        ) : (
          <div className="radar-findings-list" role="list">
            {alerts.map((alert, idx) => {
              const statusColor =
                alert.severity === 'danger'
                  ? '#C0392B'
                  : alert.severity === 'warning'
                    ? '#C17A00'
                    : '#2E7D52';
              const statusIcon =
                alert.severity === 'danger'
                  ? 'trending_down'
                  : alert.severity === 'warning'
                    ? 'help_outline'
                    : 'check_circle';
              const statusText =
                alert.severity === 'danger'
                  ? t('radar.alertSevDanger').toUpperCase()
                  : alert.severity === 'warning'
                    ? t('radar.alertSevWarning').toUpperCase()
                    : t('radar.alertSevOk').toUpperCase();

              return (
                <div
                  key={alert.id}
                  className="radar-finding-item"
                  role="listitem"
                  tabIndex={0}
                  onClick={() => onExplain(alert.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onExplain(alert.id);
                    }
                  }}
                >
                  {/* Número ordinal + ícone */}
                  <div className="radar-finding-num-col" aria-hidden="true">
                    <span className="radar-finding-num">{String(idx + 1).padStart(2, '0')}</span>
                    <div
                      className="radar-finding-icon-wrap"
                      style={{ background: statusColor + '1A' }}
                    >
                      <span
                        className="material-symbols-rounded"
                        style={{
                          fontSize: 16,
                          color: statusColor,
                          fontVariationSettings: "'FILL' 1,'wght' 600,'GRAD' 0,'opsz' 20",
                        }}
                      >
                        {statusIcon}
                      </span>
                    </div>
                  </div>

                  {/* Corpo */}
                  <div className="radar-finding-body">
                    <div className="radar-finding-status" style={{ color: statusColor }}>
                      <span
                        className="material-symbols-rounded"
                        aria-hidden="true"
                        style={{
                          fontSize: 11,
                          fontVariationSettings: "'FILL' 0,'wght' 600,'GRAD' 0,'opsz' 20",
                        }}
                      >
                        circle
                      </span>
                      {statusText}
                    </div>
                    <p className="radar-finding-title">{alert.title}</p>
                    <p className="radar-finding-line">{alert.diagnosis}</p>
                    <span className="radar-finding-action">
                      <span
                        className="material-symbols-rounded"
                        aria-hidden="true"
                        style={{ fontSize: 12 }}
                      >
                        arrow_forward
                      </span>
                      {t('radar.explainWithCross')}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// =========================================================
// Bloco 3 — card Tendência (estado honesto quando null)
// =========================================================

function TrendCard(): JSX.Element {
  // scoreTrend é null hoje (sem histórico suficiente) — estado honesto
  return (
    <div className="radar-e-card">
      <div className="radar-e-card-inner">
        <div className="radar-editorial-kicker">{t('radar.trendKicker')}</div>
        <h2 className="radar-editorial-headline">{t('radar.trendHeadline')}</h2>

        {/* Estado honesto: sem histórico ainda */}
        <div className="radar-trend-null-state" role="status" aria-live="polite">
          <span className="material-symbols-rounded radar-trend-null-icon" aria-hidden="true">
            show_chart
          </span>
          <p className="radar-trend-null-message">{t('radar.trendNullMessage')}</p>
          <p className="radar-trend-null-hint">{t('radar.trendNullHint')}</p>
        </div>
      </div>
    </div>
  );
}

// =========================================================
// Bloco 3 — card Canais
// =========================================================

function ChannelsCard({
  channels,
}: {
  readonly channels: readonly RadarChannel[] | null;
}): JSX.Element {
  return (
    <div className="radar-e-card">
      <div className="radar-e-card-inner">
        <div className="radar-editorial-kicker">{t('radar.channelsKicker')}</div>
        <h2 className="radar-editorial-headline">{t('radar.channelsHeadline')}</h2>

        {channels !== null && channels.length > 0 ? (
          <>
            {channels.length === 1 ? (
              <p className="radar-channels-single-hint">
                {t('radar.channelsSingleSource', { name: channels[0]?.name ?? '' })}
              </p>
            ) : null}
            <ChannelDonut channels={channels} />
          </>
        ) : (
          <p className="radar-null-state">{t('radar.channelsNull')}</p>
        )}
      </div>
    </div>
  );
}

// =========================================================
// Helpers de renderizacao do Hero
// =========================================================

function scoreBandLabel(band: 'bom' | 'atencao' | 'critico'): string {
  if (band === 'bom') return t('radar.heroScoreBom');
  if (band === 'atencao') return t('radar.heroScoreAtencao');
  return t('radar.heroScoreCritico');
}

function scoreBandClass(band: 'bom' | 'atencao' | 'critico'): string {
  if (band === 'bom') return 'radar-hero-band-pill radar-hero-band-bom';
  if (band === 'atencao') return 'radar-hero-band-pill radar-hero-band-atencao';
  return 'radar-hero-band-pill radar-hero-band-critico';
}

// =========================================================
// Pagina principal
// =========================================================

export function RiskRadarPage(): JSX.Element {
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [filter, setFilter] = useState<SeverityFilter>('all');
  const [reloadKey, setReloadKey] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    setState({ kind: 'loading' });
    const doLoad = async (): Promise<void> => {
      try {
        const data = await apiClient.get<RadarSnapshot>('/risk/radar');
        if (cancelled) return;
        if (isColdStart(data)) {
          setState({ kind: 'coldStart' });
          return;
        }
        setState({ kind: 'ready', data });
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 404) {
          setState({ kind: 'coldStart' });
          return;
        }
        setState({ kind: 'error' });
      }
    };
    void doLoad();
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  const openChat = (alertId: string): void => {
    navigate(`/chat?context=alert:${alertId}`);
  };

  const openChatGeneral = (): void => {
    navigate('/chat');
  };

  const openDimChat = (key: string): void => {
    navigate(`/chat?context=dimension:${key}`);
  };

  const SEVERITY_FILTERS: readonly SeverityFilter[] = ['all', 'danger', 'warning', 'ok'];

  const filteredAlerts: readonly RadarAlert[] =
    state.kind === 'ready'
      ? filter === 'all'
        ? (state.data.alerts ?? [])
        : (state.data.alerts ?? []).filter((a) => a.severity === filter)
      : [];

  // -------------------------------------------------------
  // Estado de loading
  // -------------------------------------------------------
  if (state.kind === 'loading') {
    return (
      <section aria-labelledby="radar-title" className="radar-page">
        <div className="radar-page-header">
          <div>
            <div className="radar-page-eyebrow">{t('radar.pageEyebrow')}</div>
            <h1 id="radar-title" className="radar-page-title">
              {t('radar.pageTitle')}
            </h1>
          </div>
        </div>
        <div role="status" aria-label={t('radar.loading')} className="radar-loading-skeletons">
          <Skeleton variant="card" />
          <Skeleton variant="text" width="70%" />
          <Skeleton variant="text" width="50%" />
        </div>
      </section>
    );
  }

  // -------------------------------------------------------
  // Estado de erro
  // -------------------------------------------------------
  if (state.kind === 'error') {
    return (
      <section aria-labelledby="radar-title" className="radar-page">
        <div className="radar-page-header">
          <div>
            <div className="radar-page-eyebrow">{t('radar.pageEyebrow')}</div>
            <h1 id="radar-title" className="radar-page-title">
              {t('radar.pageTitle')}
            </h1>
          </div>
        </div>
        <div role="alert" className="error">
          <p>{t('radar.error')}</p>
          <button type="button" onClick={() => setReloadKey((k) => k + 1)}>
            {t('mirror.retryLabel')}
          </button>
        </div>
      </section>
    );
  }

  // -------------------------------------------------------
  // Estado cold start
  // -------------------------------------------------------
  if (state.kind === 'coldStart') {
    return (
      <section aria-labelledby="radar-title" className="radar-page">
        <div className="radar-page-header">
          <div>
            <div className="radar-page-eyebrow">{t('radar.pageEyebrow')}</div>
            <h1 id="radar-title" className="radar-page-title">
              {t('radar.pageTitle')}
            </h1>
          </div>
        </div>
        <section className="empty-state" aria-live="polite">
          <span className="empty-state-icon material-symbols-rounded" aria-hidden="true">
            radar
          </span>
          <p>{t('radar.coldStart')}</p>
          <Link to="/sources">{t('radar.coldStartCta')}</Link>
        </section>
      </section>
    );
  }

  // -------------------------------------------------------
  // Estado ready — layout completo fiel ao prototipo
  // -------------------------------------------------------
  const { data } = state;

  const donutTotal = data.indicators?.total ?? 0;
  const donutAriaLabel =
    data.indicators !== null
      ? t('radar.donutAriaFull', {
          total: String(donutTotal),
          normal: String(data.indicators.normal),
          verificar: String(data.indicators.verificar),
          atencao: String(data.indicators.atencao),
        })
      : t('radar.donutNull');

  const sourcesFooter =
    data.connectedSources.length > 0
      ? `${t('radar.footerIndicadores', { total: String(donutTotal) })} · ${data.connectedSources.join(' · ')}`
      : t('radar.footerIndicadores', { total: String(donutTotal) });

  return (
    <section aria-labelledby="radar-title" className="radar-page">
      {/* Page header */}
      <div className="radar-page-header">
        <div>
          <div className="radar-page-eyebrow">{t('radar.pageEyebrow')}</div>
          <h1 id="radar-title" className="radar-page-title">
            {t('radar.pageTitle')}
          </h1>
          {data.connectedSources.length > 0 ? (
            <p className="radar-page-sub">
              {data.connectedSources.join(' · ')}
              {data.indicators !== null
                ? ` ${t('radar.radarSubIndicators', { total: String(donutTotal) })}`
                : null}
            </p>
          ) : null}
        </div>
        {data.period !== null ? (
          <div
            className="radar-page-meta"
            aria-label={t('radar.ariaPeriodo', { label: data.period.label })}
          >
            {data.period.label}
          </div>
        ) : null}
      </div>

      {/* ============================================================
          BLOCO 1 — HERO BAND escuro (3 colunas: score | donut | stats)
          ============================================================ */}
      <div
        className="radar-hero-band"
        aria-label={
          data.score !== null
            ? t('radar.ariaScoreGeral', { score: String(data.score) })
            : t('radar.heroScoreNull')
        }
      >
        {/* Padrao de pontos decorativo */}
        <div className="radar-hero-dots" aria-hidden="true" />

        {/* === Coluna 1: score === */}
        <div className="radar-hero-score-block">
          <div className="radar-hero-title-block">
            <div className="radar-hero-title-accent">
              <span className="radar-hero-title-line" aria-hidden="true" />
              <span className="material-symbols-rounded radar-hero-title-icon" aria-hidden="true">
                radar
              </span>
              <span className="radar-hero-title-main">{t('radar.heroTitle')}</span>
            </div>
            {data.period !== null ? (
              <div className="radar-hero-period-chip">
                <span
                  className="material-symbols-rounded"
                  aria-hidden="true"
                  style={{ fontSize: 13 }}
                >
                  calendar_month
                </span>
                {data.period.label}
              </div>
            ) : null}
          </div>

          {/* Score grande */}
          <div className="radar-hero-score-row">
            {data.score !== null ? (
              <>
                <span
                  className="radar-hero-num"
                  aria-label={t('radar.ariaScoreGeral', { score: String(data.score) })}
                >
                  {data.score}
                </span>
                <span className="radar-hero-denom" aria-hidden="true">
                  /100
                </span>
              </>
            ) : (
              <span className="radar-hero-score-null">{t('radar.heroScoreNull')}</span>
            )}
          </div>

          {/* Badge scoreBand */}
          {data.scoreBand !== null ? (
            <div className="radar-hero-qual-row">
              <span className={scoreBandClass(data.scoreBand)}>
                <span
                  className="material-symbols-rounded"
                  aria-hidden="true"
                  style={{
                    fontSize: 12,
                    fontVariationSettings: "'FILL' 1,'wght' 600,'GRAD' 0,'opsz' 20",
                  }}
                >
                  {data.scoreBand === 'bom'
                    ? 'check_circle'
                    : data.scoreBand === 'atencao'
                      ? 'warning'
                      : 'error'}
                </span>
                {scoreBandLabel(data.scoreBand)}
              </span>
            </div>
          ) : null}

          {/* Tendencia — trend e scoreTrend sao null hoje (degrade honesto) */}
          <p className="radar-hero-verdict radar-hero-verdict-muted">{t('radar.heroTrendNull')}</p>
        </div>

        <div className="radar-hero-divider" aria-hidden="true" />

        {/* === Coluna 2: donut dos indicadores com gradiente tricolor no segmento Normal === */}
        <div className="radar-hero-gauge-block">
          {data.indicators !== null ? (
            <>
              {/* SVG customizado para suportar gradiente tricolor no segmento Normal */}
              <HeroDonut indicators={data.indicators} ariaLabel={donutAriaLabel} />
              <div className="radar-donut-legend" aria-hidden="true">
                <div className="radar-donut-leg-row">
                  <span
                    className="radar-donut-leg-dot"
                    style={{ background: 'var(--md-secondary)' }}
                  />
                  <span className="radar-donut-leg-label">{t('radar.indicadoresNormal')}</span>
                  <span className="radar-donut-leg-val">{data.indicators.normal}</span>
                </div>
                <div className="radar-donut-leg-row">
                  <span className="radar-donut-leg-dot" style={{ background: '#F0A500' }} />
                  <span className="radar-donut-leg-label">{t('radar.indicadoresVerificar')}</span>
                  <span className="radar-donut-leg-val radar-donut-leg-val-warning">
                    {data.indicators.verificar}
                  </span>
                </div>
                <div className="radar-donut-leg-row">
                  <span className="radar-donut-leg-dot" style={{ background: '#FF5C4D' }} />
                  <span className="radar-donut-leg-label">{t('radar.indicadoresAtencao')}</span>
                  <span className="radar-donut-leg-val radar-donut-leg-val-danger">
                    {data.indicators.atencao}
                  </span>
                </div>
              </div>
            </>
          ) : (
            <p className="radar-donut-null">{t('radar.indicadoresNull')}</p>
          )}
        </div>

        <div className="radar-hero-divider" aria-hidden="true" />

        {/* === Coluna 3: indicadores-chave === */}
        <div className="radar-hero-stats-block">
          <p className="radar-hero-stats-title">{t('radar.indicadoresTitle').toUpperCase()}</p>

          {data.indicators !== null ? (
            <>
              {/* Atencao imediata */}
              <div className="radar-hero-stat-row">
                <div
                  className="radar-hero-stat-icon"
                  style={{ background: 'rgba(192,57,43,.18)' }}
                  aria-hidden="true"
                >
                  <span
                    className="material-symbols-rounded"
                    style={{
                      fontSize: 28,
                      color: '#FF7A6B',
                      fontVariationSettings: "'FILL' 1,'wght' 600,'GRAD' 0,'opsz' 48",
                    }}
                  >
                    warning
                  </span>
                </div>
                <div>
                  <div
                    className="radar-hero-stat-num"
                    style={{ color: '#FF7A6B' }}
                    aria-label={t('radar.ariaAtencaoImediata', {
                      n: String(data.indicators.atencao),
                    })}
                  >
                    {data.indicators.atencao}
                  </div>
                  <div className="radar-hero-stat-label">{t('radar.indicadoresAtencao')}</div>
                </div>
              </div>
              {/* A verificar */}
              <div className="radar-hero-stat-row">
                <div
                  className="radar-hero-stat-icon"
                  style={{ background: 'rgba(193,122,0,.15)' }}
                  aria-hidden="true"
                >
                  <span
                    className="material-symbols-rounded"
                    style={{
                      fontSize: 28,
                      color: '#F0A500',
                      fontVariationSettings: "'FILL' 1,'wght' 600,'GRAD' 0,'opsz' 48",
                    }}
                  >
                    help
                  </span>
                </div>
                <div>
                  <div
                    className="radar-hero-stat-num"
                    style={{ color: '#F0A500' }}
                    aria-label={t('radar.ariaAVerificar', { n: String(data.indicators.verificar) })}
                  >
                    {data.indicators.verificar}
                  </div>
                  <div className="radar-hero-stat-label">{t('radar.indicadoresVerificar')}</div>
                </div>
              </div>
              {/* Saudaveis */}
              <div className="radar-hero-stat-row" style={{ borderBottom: 'none' }}>
                <div
                  className="radar-hero-stat-icon"
                  style={{ background: 'rgba(46,125,82,.15)' }}
                  aria-hidden="true"
                >
                  <span
                    className="material-symbols-rounded"
                    style={{
                      fontSize: 28,
                      color: '#6FFBBF',
                      fontVariationSettings: "'FILL' 1,'wght' 600,'GRAD' 0,'opsz' 48",
                    }}
                  >
                    check_circle
                  </span>
                </div>
                <div>
                  <div
                    className="radar-hero-stat-num"
                    style={{ color: '#6FFBBF' }}
                    aria-label={t('radar.ariaSaudaveis', { n: String(data.indicators.normal) })}
                  >
                    {data.indicators.normal}
                  </div>
                  <div className="radar-hero-stat-label">{t('radar.indicadoresNormal')}</div>
                </div>
              </div>
            </>
          ) : (
            <p className="radar-hero-stats-null">{t('radar.indicadoresNull')}</p>
          )}

          <div className="radar-hero-footnote">
            <span
              className="material-symbols-rounded"
              aria-hidden="true"
              style={{
                fontSize: 12,
                fontVariationSettings: "'FILL' 0,'wght' 400,'GRAD' 0,'opsz' 20",
              }}
            >
              info
            </span>
            {sourcesFooter}
          </div>
        </div>
      </div>
      {/* /hero-band */}

      {/* ============================================================
          BLOCO 1B — ALERTS STRIP: "O que pede acao agora"
          ============================================================ */}
      <div className="radar-alerts-strip" style={{ marginTop: 24 }}>
        <div className="radar-alerts-strip-header">
          <div className="radar-alerts-kicker">
            <span
              className="material-symbols-rounded"
              aria-hidden="true"
              style={{
                fontSize: 12,
                fontVariationSettings: "'FILL' 1,'wght' 600,'GRAD' 0,'opsz' 20",
              }}
            >
              notifications_active
            </span>
            {t('radar.alertsSection')}
          </div>
          <h2 className="radar-alerts-headline">{t('radar.alertsSectionSub')}</h2>
          <p className="radar-alerts-guide">{t('radar.alertsSectionGuide')}</p>
        </div>

        {/* Filtros por severidade */}
        <div
          className="filters-bar radar-filters-bar"
          role="group"
          aria-label={t('radar.alertsTitle')}
        >
          {SEVERITY_FILTERS.map((sev) => {
            const label =
              sev === 'all'
                ? t('radar.filterAll')
                : sev === 'danger'
                  ? t('radar.filterDanger')
                  : sev === 'warning'
                    ? t('radar.filterWarning')
                    : t('radar.filterOk');
            return (
              <button
                key={sev}
                type="button"
                aria-pressed={filter === sev}
                className={filter === sev ? 'filter-btn filter-btn-active' : 'filter-btn'}
                onClick={() => setFilter(sev)}
              >
                {label}
              </button>
            );
          })}
        </div>

        {/* Lista de alertas — nova linha densa fiel ao prototipo */}
        {filteredAlerts.length === 0 ? (
          <p className="muted radar-alerts-empty">{t('radar.alertEmptyFilter')}</p>
        ) : (
          <div className="radar-alert-row-list" role="list" aria-label={t('radar.alertsSection')}>
            {filteredAlerts.map((alert, idx) => (
              <div key={alert.id} role="listitem">
                <AlertCard alert={alert} index={idx} onExplain={openChat} />
              </div>
            ))}
          </div>
        )}
      </div>
      {/* /alerts-strip */}

      {/* ============================================================
          BLOCO 2 — RADAR DE DIMENSÕES + ACHADOS
          ============================================================ */}
      <div
        className="radar-main-grid"
        style={{ marginTop: 24 }}
        aria-label="Radar de dimensoes e achados"
      >
        <DimensionsCard
          dimensions={data.dimensions ?? null}
          connectedSources={data.connectedSources}
          onDimClick={openDimChat}
        />
        <FindingsCard alerts={(data.alerts ?? []).slice(0, 5)} onExplain={openChat} />
      </div>

      {/* ============================================================
          BLOCO 3 — TENDÊNCIA + CANAIS
          ============================================================ */}
      <div
        className="radar-second-row"
        style={{ marginTop: 24 }}
        aria-label="Tendencia e distribuicao por canal"
      >
        <TrendCard />
        <ChannelsCard channels={data.channels ?? null} />
      </div>

      {/* ============================================================
          BLOCO 4 — GRADE DE INDICADORES
          ============================================================ */}
      <div className="radar-indicators-section" style={{ marginTop: 32 }}>
        <div className="radar-indicators-section-header">
          <div className="radar-editorial-kicker">
            <span
              className="material-symbols-rounded"
              aria-hidden="true"
              style={{
                fontSize: 12,
                fontVariationSettings: "'FILL' 1,'wght' 600,'GRAD' 0,'opsz' 20",
              }}
            >
              table_chart
            </span>
            {t('radar.indicatorsGridKicker')}
          </div>
          <h2 className="radar-editorial-headline">{t('radar.indicatorsGridHeadline')}</h2>
          <p className="radar-editorial-guide">{t('radar.indicatorsGridGuide')}</p>
        </div>
        <IndicatorsGrid items={data.indicatorsList ?? []} />
      </div>

      {/* ============================================================
          FAB — Perguntar a CROSS (fixo canto inferior direito)
          ============================================================ */}
      <Fab label={t('radar.fabLabel')} icon="forum" onClick={openChatGeneral} />
    </section>
  );
}

// =========================================================
// HeroDonut — donut SVG customizado com gradiente tricolor no Normal
// Necessário para o segmento "Normal" usar o gradiente de marca (#3F2570→#0079DC→#F9005C)
// em vez de azul sólido (correção item 1a do brief).
// =========================================================

function HeroDonut({
  indicators,
  ariaLabel,
}: {
  readonly indicators: RadarIndicators;
  readonly ariaLabel: string;
}): JSX.Element {
  const SIZE = 136;
  const CX = SIZE / 2;
  const CY = SIZE / 2;
  const STROKE_W = 16;
  const R = (SIZE - STROKE_W * 2) / 2;
  const CIRCUMFERENCE = 2 * Math.PI * R;
  const GAP = 3;

  const total = indicators.total > 0 ? indicators.total : 1;

  type Seg = { dashLength: number; dashOffset: number };
  function calcSeg(value: number, used: number): Seg {
    const frac = value / total;
    const dashLength = Math.max(0, frac * CIRCUMFERENCE - GAP);
    const dashOffset = -(used - CIRCUMFERENCE / 4);
    return { dashLength, dashOffset };
  }

  const normalUsed = 0;
  const normalSeg = calcSeg(indicators.normal, normalUsed);
  const verificarUsed = (indicators.normal / total) * CIRCUMFERENCE;
  const verificarSeg = calcSeg(indicators.verificar, verificarUsed);
  const atencaoUsed = verificarUsed + (indicators.verificar / total) * CIRCUMFERENCE;
  const atencaoSeg = calcSeg(indicators.atencao, atencaoUsed);

  return (
    <svg
      viewBox={`0 0 ${SIZE} ${SIZE}`}
      width={SIZE}
      height={SIZE}
      role="img"
      aria-label={ariaLabel}
      focusable="false"
    >
      <defs>
        {/* Gradiente tricolor da marca para o segmento Normal */}
        <linearGradient id="hg-brand" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#3F2570" />
          <stop offset="50%" stopColor="#0079DC" />
          <stop offset="100%" stopColor="#F9005C" />
        </linearGradient>
      </defs>

      {/* Trilha */}
      <circle
        cx={CX}
        cy={CY}
        r={R}
        fill="none"
        stroke="rgba(255,255,255,0.07)"
        strokeWidth={STROKE_W}
      />

      {/* Normal — gradiente tricolor de marca */}
      <circle
        cx={CX}
        cy={CY}
        r={R}
        fill="none"
        stroke="url(#hg-brand)"
        strokeWidth={STROKE_W}
        strokeDasharray={`${normalSeg.dashLength.toFixed(2)} ${CIRCUMFERENCE.toFixed(2)}`}
        strokeDashoffset={normalSeg.dashOffset.toFixed(2)}
        strokeLinecap="butt"
        style={{
          opacity: 0,
          animation: 'donutSegFadeIn 900ms cubic-bezier(0.05,0.7,0.1,1.0) 360ms forwards',
        }}
      />

      {/* Atenção — vermelho */}
      <circle
        cx={CX}
        cy={CY}
        r={R}
        fill="none"
        stroke="#D63222"
        strokeWidth={STROKE_W}
        strokeDasharray={`${atencaoSeg.dashLength.toFixed(2)} ${CIRCUMFERENCE.toFixed(2)}`}
        strokeDashoffset={atencaoSeg.dashOffset.toFixed(2)}
        strokeLinecap="butt"
        style={{
          opacity: 0,
          animation: 'donutSegFadeIn 700ms cubic-bezier(0.05,0.7,0.1,1.0) 500ms forwards',
        }}
      />

      {/* Verificar — âmbar */}
      <circle
        cx={CX}
        cy={CY}
        r={R}
        fill="none"
        stroke="#C17A00"
        strokeWidth={STROKE_W}
        strokeDasharray={`${verificarSeg.dashLength.toFixed(2)} ${CIRCUMFERENCE.toFixed(2)}`}
        strokeDashoffset={verificarSeg.dashOffset.toFixed(2)}
        strokeLinecap="butt"
        style={{
          opacity: 0,
          animation: 'donutSegFadeIn 700ms cubic-bezier(0.05,0.7,0.1,1.0) 570ms forwards',
        }}
      />

      {/* Centro: total */}
      <text
        x={CX}
        y={CY - 4}
        textAnchor="middle"
        fontFamily="'Space Grotesk', sans-serif"
        fontSize="26"
        fontWeight="700"
        fill="#FFFFFF"
      >
        {indicators.total}
      </text>
      <text
        x={CX}
        y={CY + 12}
        textAnchor="middle"
        fontFamily="'Roboto Flex', sans-serif"
        fontSize="9.5"
        fill="rgba(255,255,255,0.50)"
      >
        {t('radar.donutCenterLabel')}
      </text>
    </svg>
  );
}
